import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import type {
  GazePoint,
  HeatmapGenerationConfig,
  VideoHeatmapConfig,
  PrecomputedHeatmapFrame,
} from "../types/gazeHeatmapTypes";
import { getWeightedPointsForWindow } from "../utils/coordinateConversion";
import { DEFAULT_HEATMAP_CONFIG } from "../utils/heatmapGeneration";

/** Default video heatmap configuration */
export const DEFAULT_VIDEO_HEATMAP_CONFIG: VideoHeatmapConfig = {
  windowDuration: 3.0, // 3 seconds sliding window
  fadeOutRatio: 0.6, // 60% of window fades out
  keyframeInterval: 0.5, // One keyframe every 0.5s
};

// Stable empty objects for default parameters (avoid creating new references)
const EMPTY_CONFIG: Partial<HeatmapGenerationConfig> = {};
const EMPTY_VIDEO_CONFIG: Partial<VideoHeatmapConfig> = {};

interface UseVideoSyncedHeatmapParams {
  points: GazePoint[];
  videoDuration: number;
  currentTime: number;
  config?: Partial<HeatmapGenerationConfig>;
  videoConfig?: Partial<VideoHeatmapConfig>;
  enabled?: boolean;
  sceneRotationY?: number;
}

interface UseVideoSyncedHeatmapResult {
  currentHeatmap: ImageData | null;
  isPrecomputing: boolean;
  precomputeProgress: number;
  error: string | null;
  isReady: boolean;
}

/**
 * Hook to generate and sync heatmaps with video playback
 * Pre-computes keyframes at mount, then selects nearest keyframe during playback
 */
export function useVideoSyncedHeatmap({
  points,
  videoDuration,
  currentTime,
  config = EMPTY_CONFIG,
  videoConfig = EMPTY_VIDEO_CONFIG,
  enabled = true,
  sceneRotationY = 0,
}: UseVideoSyncedHeatmapParams): UseVideoSyncedHeatmapResult {
  const [currentHeatmap, setCurrentHeatmap] = useState<ImageData | null>(null);
  const [isPrecomputing, setIsPrecomputing] = useState(false);
  const [precomputeProgress, setPrecomputeProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const workerRef = useRef<Worker | null>(null);
  const cacheRef = useRef<PrecomputedHeatmapFrame[]>([]);
  const precomputeIdRef = useRef(0);

  // Merge configs
  const fullConfig: HeatmapGenerationConfig = useMemo(
    () => ({
      ...DEFAULT_HEATMAP_CONFIG,
      ...config,
    }),
    [config],
  );

  const fullVideoConfig: VideoHeatmapConfig = useMemo(
    () => ({
      ...DEFAULT_VIDEO_HEATMAP_CONFIG,
      ...videoConfig,
    }),
    [videoConfig],
  );

  // Calculate keyframe times
  const keyframeTimes = useMemo(() => {
    if (videoDuration <= 0) return [];
    const times: number[] = [];
    let t = 0;
    while (t <= videoDuration) {
      times.push(t);
      t += fullVideoConfig.keyframeInterval;
    }
    // Ensure we have the last frame
    if (times[times.length - 1] < videoDuration) {
      times.push(videoDuration);
    }
    return times;
  }, [videoDuration, fullVideoConfig.keyframeInterval]);

  // Prepare weighted points for each keyframe
  // NOTE: isVideo360 = true because this hook is only used for video 360 content
  // This ensures we use the AVProVideo shader UV formula instead of the image 360 formula
  const prepareKeyframes = useCallback(() => {
    return keyframeTimes.map((timeCenter) => ({
      timeCenter,
      points: getWeightedPointsForWindow(
        points,
        timeCenter,
        fullVideoConfig.windowDuration,
        fullVideoConfig.fadeOutRatio,
        sceneRotationY,
        true, // isVideo360 = true - use AVProVideo shader formula
      ),
    }));
  }, [keyframeTimes, points, fullVideoConfig, sceneRotationY]);

  // Pre-compute all keyframes
  useEffect(() => {
    if (!enabled || points.length === 0 || videoDuration <= 0 || keyframeTimes.length === 0) {
      setIsReady(false);
      setCurrentHeatmap(null);
      cacheRef.current = [];
      return undefined;
    }

    // Increment precompute ID for race condition handling
    precomputeIdRef.current += 1;
    const currentPrecomputeId = precomputeIdRef.current;

    setIsPrecomputing(true);
    setPrecomputeProgress(0);
    setError(null);
    setIsReady(false);
    cacheRef.current = [];

    // Prepare keyframe data
    const keyframes = prepareKeyframes();

    try {
      // Create worker if not exists
      if (!workerRef.current) {
        workerRef.current = new Worker(new URL("../utils/heatmapWorker.ts", import.meta.url), {
          type: "module",
        });
      }

      const worker = workerRef.current;

      worker.onmessage = (e) => {
        if (precomputeIdRef.current !== currentPrecomputeId) return;

        const { type, results, progress, error: workerError } = e.data;

        if (type === "batch-progress") {
          setPrecomputeProgress(progress);
        } else if (type === "batch-result") {
          // Store results in cache
          cacheRef.current = results;
          setIsPrecomputing(false);
          setPrecomputeProgress(100);
          setIsReady(true);
        } else if (type === "error") {
          console.error("[useVideoSyncedHeatmap] Worker error:", workerError);
          setError(workerError);
          setIsPrecomputing(false);
        }
      };

      worker.onerror = (e) => {
        if (precomputeIdRef.current !== currentPrecomputeId) return;
        setError(e.message || "Worker error");
        setIsPrecomputing(false);
      };

      // Send batch generation request
      worker.postMessage({
        type: "generate-batch",
        keyframes,
        config: fullConfig,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Pre-computation failed");
      setIsPrecomputing(false);
    }

    return () => {
      // Cleanup handled in separate effect
    };
  }, [enabled, points, videoDuration, keyframeTimes, fullConfig, prepareKeyframes]);

  // Select nearest keyframe based on current time
  useEffect(() => {
    if (!isReady || cacheRef.current.length === 0) return;

    // Find nearest keyframe
    const keyframeInterval = fullVideoConfig.keyframeInterval;
    const nearestIndex = Math.round(currentTime / keyframeInterval);
    const clampedIndex = Math.max(0, Math.min(cacheRef.current.length - 1, nearestIndex));

    const frame = cacheRef.current[clampedIndex];
    if (frame) {
      // Reconstruct ImageData from the cached array
      const imageData = new ImageData(
        new Uint8ClampedArray(frame.imageDataArray),
        frame.width,
        frame.height,
      );
      setCurrentHeatmap(imageData);
    }
  }, [currentTime, isReady, fullVideoConfig.keyframeInterval]);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  return {
    currentHeatmap,
    isPrecomputing,
    precomputeProgress,
    error,
    isReady,
  };
}
